import type { AccountInfo, InteractionType } from '@azure/msal-browser';
import { useMsalStore } from '../stores/msal-store';
import {
    toValue,
    type MaybeRefOrGetter,
    computed,
    type ComputedRef,
} from 'vue';

export interface LogoutOptions {
    postLogoutRedirectUri?: string;
    interactionType?: InteractionType.Popup | InteractionType.Redirect;
    account?: AccountInfo | null;
}

export interface UseLogoutOptions {
    postLogoutRedirectUri?: MaybeRefOrGetter<string | null | undefined>;
    interactionType?: MaybeRefOrGetter<
        InteractionType.Popup | InteractionType.Redirect | null | undefined
    >;
    account?: MaybeRefOrGetter<AccountInfo | null | undefined>;
}

export interface UseLogoutResult {
    logout: (overwriteOptions?: LogoutOptions) => Promise<void>;
    loading: ComputedRef<boolean>;
    disabled: ComputedRef<boolean>;
}

export function useLogout(options: UseLogoutOptions = {}): UseLogoutResult {
    const store = useMsalStore();
    const loading = computed(() => store.logoutLoading);
    const disabled = computed(() => !store.idle);

    async function logout(overwriteOptions: LogoutOptions = {}) {
        if (loading.value) {
            console.warn(
                'Tried to start login while another login was still loading.'
            );
            return;
        }

        if (disabled.value) {
            console.warn('Tried to start login while msal was not (yet) idle.');
            return;
        }

        await store.logout({
            postLogoutRedirectUri:
                overwriteOptions.postLogoutRedirectUri ??
                toValue(options.postLogoutRedirectUri) ??
                undefined,
            interactionType:
                overwriteOptions.interactionType ??
                toValue(options.interactionType) ??
                undefined,
            account: overwriteOptions.account ?? toValue(options.account),
        });
    }

    return { logout, loading, disabled };
}
